import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { COUNTRIES } from "@/lib/countries";
import {
  EXPERIENCE_OPTIONS,
  type HomepageLeadModalProps,
  useHomepageLeadModal,
  YEAR_OF_EXPERIENCE_OPTIONS,
} from "@/hooks/use-homepage-lead-modal";
import OptInCheckboxes from "@/components/forms/OptInCheckboxes";

function countryFlag(code: string): string {
  return code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

function FieldError({ message, id }: { message?: string; id: string }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="mt-1 text-xs text-red-400">
      {message}
    </p>
  );
}

const surfaceCard = "border-white/10 bg-[#282828]";
const inputClasses = `w-full rounded-lg border ${surfaceCard} px-4 py-3 text-white placeholder:text-secondary/60 focus:border-white/35 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/25`;
const selectClasses = `w-full appearance-none rounded-lg border ${surfaceCard} px-4 py-3 text-left text-white focus:border-white/35 focus:outline-none focus-visible:ring-1 focus-visible:ring-white/25`;
const dropdownItemClasses =
  "w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 hover:text-white";

function stepHeader(step: number, codingPathStep?: boolean): { title: string; subtitle: string } {
  if (codingPathStep) {
    switch (step) {
      case 0: return { title: "How do we reach you?", subtitle: "We'll send your WhatsApp invite to this number." };
      case 1: return { title: "Choose your Learning Path", subtitle: "Which AI learning path aligns with your goal?" };
      case 2: return { title: "A bit about you.", subtitle: "Helps us route you to the right group." };
      case 3: return { title: "Two quick things.", subtitle: "Both free. Uncheck either one if you'd prefer." };
      default: return { title: "", subtitle: "" };
    }
  }
  switch (step) {
    case 0: return { title: "How do we reach you?", subtitle: "We'll send your WhatsApp invite to this number." };
    case 1: return { title: "A bit about you.", subtitle: "Helps us route you to the right group." };
    case 2: return { title: "Two quick things.", subtitle: "Both free. Uncheck either one if you'd prefer." };
    default: return { title: "", subtitle: "" };
  }
}

export default function HomepageLeadModal({
  open,
  initialEmail,
  onClose,
  formConfig,
  codingPathStep,
}: HomepageLeadModalProps) {
  const lead = useHomepageLeadModal({
    open,
    initialEmail,
    onClose,
    formConfig,
    codingPathStep,
  });

  const modalBodyScrollRef = useRef<HTMLDivElement>(null);
  const dialCodeRef = useRef<HTMLDivElement>(null);
  const dialCodeBtnRef = useRef<HTMLButtonElement>(null);
  const [dialCodePos, setDialCodePos] = useState({ top: 0, left: 0 });
  const successSectionRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!lead.successData) return;
    successSectionRef.current?.scrollIntoView({ behavior: "auto", block: "center" });
  }, [lead.successData]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const t = e.target as Node;
      if (
        dialCodeRef.current &&
        !dialCodeRef.current.contains(t) &&
        dialCodeBtnRef.current &&
        !dialCodeBtnRef.current.contains(t)
      ) {
        lead.setDialCodeOpen(false);
      }
    }
    if (open) document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead.setDialCodeOpen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !lead.successData) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, lead.successData]);

  if (!open) return null;

  const progressPct =
    (lead.displayStepNumber / Math.max(lead.displayTotalSteps, 1)) * 100;
  const copy = stepHeader(lead.step, codingPathStep);
  const stepLabel = `STEP ${lead.displayStepNumber} OF ${lead.displayTotalSteps}`;

  const experienceOptions =
    lead.professionalExperienceOptions ??
    EXPERIENCE_OPTIONS.map((t) => ({ optionId: -1, title: t }));

  const content = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6 sm:p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        aria-label="Close"
        onClick={() => !lead.loading && !lead.successData && onClose()}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="homepage-lead-modal-title"
        className="relative z-[1] flex max-h-[min(calc(100dvh-3rem),40rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#212121] shadow-[0_0_40px_rgba(0,0,0,0.6)] sm:max-h-[90vh]"
        style={{ fontFamily: "var(--font-fira)" }}
      >
        {/* Progress */}
        <div className="h-1 w-full shrink-0 bg-white/15">
          <div
            className="h-full bg-white transition-[width] duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
          />
        </div>

        <div className="flex items-start justify-between gap-3 border-b border-white/5 px-5 pb-3 pt-4 sm:px-6 sm:pt-5">
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-white/45">
            {stepLabel}
          </p>
          <button
            type="button"
            onClick={() => !lead.loading && !lead.successData && onClose()}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-white/80 transition hover:bg-white/10"
            aria-label="Close dialog"
          >
            <span className="text-lg leading-none" aria-hidden>
              ×
            </span>
          </button>
        </div>

        <div
          ref={modalBodyScrollRef}
          className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5"
        >
          {lead.successData ? (
            <section
              ref={successSectionRef}
              aria-live="polite"
              className="flex flex-col items-center justify-center gap-4 py-8 text-center"
            >
              <div className="relative flex h-16 w-16 items-center justify-center rounded-full border-2 border-white/35">
                <svg
                  className="h-8 w-8 text-white/35"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    className="opacity-25"
                    d="M12 3a9 9 0 109 9"
                    strokeLinecap="round"
                  />
                </svg>
                <svg
                  className="absolute h-8 w-8 animate-spin text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              </div>
              <p className="text-sm text-secondary">Redirecting…</p>
            </section>
          ) : (
            <form
              onSubmit={(e) => e.preventDefault()}
              noValidate
              className="flex flex-col gap-5"
            >
              <input
                id="email"
                name="email"
                type="hidden"
                value={lead.email}
                readOnly
                aria-hidden="true"
              />

              <div>
                <h2
                  id="homepage-lead-modal-title"
                  className="font-fira-condensed text-2xl font-medium leading-tight tracking-tight text-white sm:text-[28px]"
                >
                  {copy.title}
                </h2>
                <p className="mt-2 text-sm leading-snug text-[#FFFFFF85]">
                  {copy.subtitle}
                </p>
              </div>

              {/* Step 0: Name + Phone */}
              {lead.step === 0 && (
                <div className="flex flex-col gap-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="mb-2 block text-xs font-semibold text-white/55"
                    >
                      Your first name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="given-name"
                      placeholder="Your name"
                      value={lead.name}
                      onChange={(e) => lead.setName(e.target.value)}
                      aria-describedby="modal-name-error"
                      aria-invalid={!!lead.fieldErrors.name}
                      className={`${inputClasses} ${lead.fieldErrors.name ? "border-red-400/50" : ""}`}
                    />
                    <FieldError
                      id="modal-name-error"
                      message={lead.fieldErrors.name}
                    />
                  </div>

                  <div>
                    <span id="country-label" className="sr-only">
                      Country
                    </span>
                    {!lead.geo.loaded ? (
                      <div
                        className={`flex h-12 items-center rounded-lg px-4 ${surfaceCard}`}
                      >
                        <div className="h-4 w-40 max-w-full animate-pulse rounded bg-gray-700" />
                      </div>
                    ) : lead.countryEditable ? (
                      <>
                        <label
                          htmlFor="country-select"
                          className="mb-2 block text-xs font-semibold text-white/55"
                        >
                          Where are you based?
                        </label>
                        <select
                          id="country-select"
                          value={lead.countryCode}
                          onChange={(e) =>
                            lead.handleCountryChange(e.target.value)
                          }
                          aria-labelledby="country-label"
                          className={selectClasses}
                        >
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </>
                    ) : (
                      <>
                        <p className="mb-2 text-xs font-semibold text-white/55">
                          Where are you based?
                        </p>
                        <div
                          className={`flex h-12 items-center justify-between rounded-lg px-4 ${surfaceCard}`}
                        >
                          <span className="text-sm text-white">
                            <span aria-hidden>📍</span> {lead.country}
                          </span>
                          <button
                            type="button"
                            onClick={() => lead.setCountryEditable(true)}
                            className="text-xs font-medium text-white/90 underline decoration-white/30 underline-offset-2 hover:decoration-white"
                          >
                            Change
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="mb-2 block text-xs font-semibold text-white/55"
                    >
                      Phone number
                    </label>
                    <div
                      className={`flex items-center rounded-lg border bg-[#282828] transition-colors ${
                        lead.fieldErrors.phone
                          ? "border-red-400/50"
                          : "border-white/10"
                      }`}
                    >
                      <div className="relative shrink-0">
                        <button
                          id="dial-code-btn"
                          ref={dialCodeBtnRef}
                          type="button"
                          onClick={() => {
                            if (!lead.dialCodeOpen && dialCodeBtnRef.current) {
                              const rect =
                                dialCodeBtnRef.current.getBoundingClientRect();
                              setDialCodePos({
                                top: rect.bottom + 4,
                                left: rect.left,
                              });
                            }
                            lead.setDialCodeOpen(!lead.dialCodeOpen);
                          }}
                          aria-expanded={lead.dialCodeOpen}
                          aria-haspopup="listbox"
                          className="flex items-center gap-1.5 border-none bg-transparent py-3 pl-3 pr-2 text-white outline-none"
                        >
                          <span>{countryFlag(lead.dialCountryCode)}</span>
                          <span className="text-sm">{lead.dialCode}</span>
                          <svg
                            className={`h-3.5 w-3.5 text-white/50 ${lead.dialCodeOpen ? "rotate-180" : ""}`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path d="M6 9l6 6 6-6" />
                          </svg>
                        </button>
                      </div>
                      <div className="h-5 w-px shrink-0 bg-white/10" />
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        maxLength={lead.phoneMax}
                        placeholder="Phone number"
                        value={lead.phone}
                        onChange={(e) => {
                          const d = e.target.value.replace(/\D/g, "");
                          if (d.length <= lead.phoneMax) lead.setPhone(d);
                        }}
                        aria-describedby="modal-phone-error"
                        aria-invalid={!!lead.fieldErrors.phone}
                        className="min-w-0 flex-1 border-none bg-transparent py-3 pl-3 pr-4 text-white outline-none placeholder:text-secondary/60"
                      />
                    </div>
                    <p className="mt-1.5 text-[11px] text-white/40">
                      We&apos;ll send your WhatsApp invite link here.
                    </p>
                    <FieldError
                      id="modal-phone-error"
                      message={lead.fieldErrors.phone}
                    />
                  </div>

                </div>
              )}

              {/* Step 1 (codingPathStep): Learning path cards — auto-advances on selection */}
              {lead.step === 1 && codingPathStep && (
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-3">
                    {([
                      {
                        val: "no" as const,
                        title: "No-code",
                        subtitle: "No-code tools and low-code",
                        icon: (
                          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
                            <rect x="4" y="10" width="28" height="4" rx="1.5" fill="currentColor" opacity="0.5" />
                            <rect x="4" y="16" width="28" height="12" rx="2" fill="currentColor" opacity="0.9" />
                          </svg>
                        ),
                      },
                      {
                        val: "yes" as const,
                        title: "I know how to code",
                        subtitle: "Python, minimum 3+ years",
                        icon: (
                          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true">
                            <rect x="3" y="3" width="30" height="30" rx="4" stroke="currentColor" strokeWidth="2" opacity="0.6" />
                            <path d="M13 13l-5 5 5 5M23 13l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        ),
                      },
                    ]).map(({ val, title, subtitle, icon }) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => lead.selectLearningPath(val)}
                        className={`flex flex-col gap-4 rounded-xl border p-5 text-left transition-all cursor-pointer ${
                          lead.knowCoding === val
                            ? "border-white/50 bg-white/10"
                            : "border-white/15 bg-white/[0.03] hover:border-white/30 hover:bg-white/[0.06]"
                        }`}
                      >
                        <span className="text-white/80">{icon}</span>
                        <span className="flex flex-col gap-1">
                          <span className="text-sm font-semibold text-white leading-snug">{title}</span>
                          <span className="text-xs text-white/50 leading-snug">{subtitle}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                  <FieldError id="modal-knowcoding-error" message={lead.fieldErrors.knowCoding} />
                </div>
              )}

              {/* Step 1 (normal) or Step 2 (codingPathStep): Know Coding checkbox + Experience + YOE */}
              {((lead.step === 1 && !codingPathStep) || (lead.step === 2 && codingPathStep)) && (
                <div className="flex flex-col gap-5">
                  {!codingPathStep && (
                    <div>
                      <p className="mb-3 text-sm text-white/90">
                        How do you prefer to{" "}
                        <span className="font-medium text-white">learn AI</span>?
                      </p>
                      <div className="flex gap-3">
                        {([
                          { val: "yes", label: "With Coding" },
                          { val: "no", label: "Without Coding" },
                        ] as const).map(({ val, label }) => (
                          <button
                            key={val}
                            type="button"
                            onClick={() => lead.setKnowCoding(val)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm transition-all cursor-pointer ${
                              lead.knowCoding === val
                                ? "bg-white text-black border-white font-medium"
                                : "bg-transparent text-white/80 border-white/30 hover:border-white/60"
                            }`}
                          >
                            <span className={`w-4 h-4 flex items-center justify-center rounded border flex-shrink-0 ${lead.knowCoding === val ? "bg-black border-black" : "border-current opacity-50"}`}>
                              {lead.knowCoding === val && (
                                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </span>
                            {label}
                          </button>
                        ))}
                      </div>
                      <FieldError id="modal-knowcoding-error" message={lead.fieldErrors.knowCoding} />
                    </div>
                  )}

                  <div>
                    <p className="mb-3 text-sm text-white/90">
                      What{" "}
                      <span className="font-medium text-white">describes you</span>{" "}
                      best?
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {experienceOptions.map((opt) => (
                        <button
                          key={`${opt.optionId}-${opt.title}`}
                          type="button"
                          onClick={() => {
                            const wasStudent = lead.experience === "Student";
                            lead.setExperience(opt.title);
                            lead.setExperienceOptionId(opt.optionId >= 0 ? opt.optionId : null);
                            if (opt.title === "Student") {
                              lead.setYearOfExperience("0 years (Fresher)");
                            } else if (wasStudent) {
                              lead.setYearOfExperience("");
                            }
                          }}
                          className={`rounded-lg border px-3 py-2 text-sm transition ${
                            lead.experience === opt.title
                              ? "border-white/60 bg-white/10 text-white"
                              : "border-white/15 bg-transparent text-white/70 hover:border-white/30 hover:text-white/90"
                          }`}
                        >
                          {opt.title}
                        </button>
                      ))}
                    </div>
                    <FieldError id="modal-experience-error" message={lead.fieldErrors.experience} />
                  </div>

                  {lead.experience && lead.experience !== "Student" && (
                    <div>
                      <p className="mb-3 text-sm text-white/90">
                        How many{" "}
                        <span className="font-medium text-white">years of experience</span>?
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {YEAR_OF_EXPERIENCE_OPTIONS.map((opt) => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => lead.setYearOfExperience(opt)}
                            className={`rounded-lg border px-3 py-2 text-sm transition ${
                              lead.yearOfExperience === opt
                                ? "border-white/60 bg-white/10 text-white"
                                : "border-white/15 bg-transparent text-white/70 hover:border-white/30 hover:text-white/90"
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                      <FieldError id="modal-yoe-error" message={lead.fieldErrors.yearOfExperience} />
                    </div>
                  )}
                </div>
              )}

              {/* Step 2 (normal) or Step 3 (codingPathStep): Opt-ins */}
              {((lead.step === 2 && !codingPathStep) || (lead.step === 3 && codingPathStep)) && (
                <div className="flex flex-col gap-3">
                  <OptInCheckboxes
                    enrollMastermind={lead.enrollMastermind}
                    subscribeNewsletter={lead.subscribeNewsletter}
                    setEnrollMastermind={lead.setEnrollMastermind}
                    setSubscribeNewsletter={lead.setSubscribeNewsletter}
                    mastermindRowClassName={`rounded-xl border p-4 transition-colors hover:bg-white/[0.04] ${surfaceCard}`}
                    newsletterRowClassName={`rounded-xl border p-4 transition-colors hover:bg-white/[0.04] ${surfaceCard}`}
                    mastermindLabelClassName="text-sm font-medium leading-snug text-white"
                    newsletterLabelClassName="text-sm leading-snug text-white/90"
                  />
                </div>
              )}

              {lead.error ? (
                <div
                  role="alert"
                  className="rounded-lg border border-red-400/20 bg-red-500/10 px-3 py-2 text-center text-sm text-red-300"
                >
                  {lead.error}
                </div>
              ) : null}

              <div
                className={`flex flex-row items-center gap-2 pt-1 sm:gap-3 ${lead.step > 0 ? "justify-between" : ""}`}
              >
                {lead.step > 0 ? (
                  <button
                    type="button"
                    onClick={lead.goBack}
                    disabled={lead.loading}
                    className="shrink-0 rounded-[8px] border border-white/20 bg-transparent px-4 py-3 text-sm font-medium text-white/70 transition hover:bg-white/5 sm:px-5"
                  >
                    Back
                  </button>
                ) : null}
                {lead.step < lead.displayTotalSteps - 1 && !(codingPathStep && lead.step === 1) ? (
                  <button
                    type="button"
                    onClick={lead.goNext}
                    disabled={!lead.isCurrentStepValid || lead.loading}
                    className={`min-w-0 rounded-[8px] bg-white px-4 py-3.5 text-base font-semibold text-black transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-45 sm:min-w-[200px] sm:px-6 ${lead.step > 0 ? "flex-1" : "w-full"}`}
                  >
                    Continue
                  </button>
                ) : lead.step === lead.displayTotalSteps - 1 ? (
                  <button
                    type="button"
                    disabled={lead.loading || !lead.isCurrentStepValid}
                    onClick={() => void lead.handleFinalSubmit()}
                    className={`min-w-0 rounded-[8px] bg-white px-4 py-3.5 text-base font-semibold text-black transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-45 sm:min-w-[200px] sm:px-6 ${lead.step > 0 ? "flex-1" : "w-full"}`}
                  >
                    {lead.loading ? (
                      <>
                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                        Joining…
                      </>
                    ) : (
                      "Get Instant Access →"
                    )}
                  </button>
                ) : null}
              </div>

              <p className="flex items-center justify-center gap-1.5 pb-2 text-center text-xs text-secondary">
                <svg
                  className="h-3.5 w-3.5 shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Your data is safe. No spam, ever.
              </p>
            </form>
          )}
        </div>
      </div>

      {lead.dialCodeOpen ? (
        <div
          ref={dialCodeRef}
          role="listbox"
          className={`fixed z-[210] w-64 max-h-[min(15rem,40vh)] overflow-y-auto rounded-lg py-1 shadow-xl dropdown-scroll ${surfaceCard}`}
          style={{ top: dialCodePos.top, left: dialCodePos.left }}
        >
          {COUNTRIES.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => {
                lead.setDialCode(c.dialCode);
                lead.setDialCountryCode(c.code);
                lead.setDialCodeOpen(false);
                const max = Array.isArray(c.phoneLength)
                  ? c.phoneLength[1]
                  : c.phoneLength;
                if (lead.phone.length > max)
                  lead.setPhone(lead.phone.slice(0, max));
              }}
              className={dropdownItemClasses}
            >
              <span className="mr-2">{countryFlag(c.code)}</span>
              {c.name} ({c.dialCode})
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  return createPortal(content, document.body);
}